/**
 * Activity Feed Service
 *
 * Handles fetching and managing activity feed entries from Supabase.
 */

import { supabase } from '@/lib/supabase';
import { ActivityFeedEntry, ActivityType } from '../types';
import {
  getActivityFeedEnabledTypes,
  getActivityFeedDashboardCount,
} from '@/services/systemSettings';

interface FetchActivityFeedOptions {
  limit?: number;
  offset?: number;
  enabledTypesOnly?: boolean;
}

/**
 * Check and create any missing SSW milestones for a pregnancy
 */
export async function checkAndCreateSSWMilestones(
  pregnancyId: string
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('check_and_create_ssw_milestones', {
      p_pregnancy_id: pregnancyId,
    });

    if (error) {
      console.error('Error checking SSW milestones:', error);
      return 0;
    }

    return data || 0;
  } catch (error) {
    console.error('Error in checkAndCreateSSWMilestones:', error);
    return 0;
  }
}

/**
 * Check and create any missing SSW change entries for a pregnancy
 * This creates an entry for every new SSW with baby development info
 */
export async function checkAndCreateSSWChanges(
  pregnancyId: string
): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('check_and_create_ssw_changes', {
      p_pregnancy_id: pregnancyId,
    });

    if (error) {
      // Function might not exist yet (before migration is applied)
      if (error.code === '42883') {
        console.log('SSW changes function not yet available');
        return 0;
      }
      console.error('Error checking SSW changes:', error);
      return 0;
    }

    return data || 0;
  } catch (error) {
    console.error('Error in checkAndCreateSSWChanges:', error);
    return 0;
  }
}

/**
 * Fetch activity feed entries for the current user
 */
export async function fetchActivityFeed(
  userId: string,
  options: FetchActivityFeedOptions = {}
): Promise<ActivityFeedEntry[]> {
  const {
    limit = 10,
    offset = 0,
    enabledTypesOnly = true,
  } = options;

  try {
    let enabledTypes: string[] | null = null;

    if (enabledTypesOnly) {
      enabledTypes = await getActivityFeedEnabledTypes();
    }

    const { data, error } = await supabase.rpc('get_activity_feed', {
      p_user_id: userId,
      p_limit: limit,
      p_offset: offset,
      p_enabled_types: enabledTypes,
    });

    if (error) {
      console.error('Error fetching activity feed:', error);
      return [];
    }

    return (data || []) as ActivityFeedEntry[];
  } catch (error) {
    console.error('Error in fetchActivityFeed:', error);
    return [];
  }
}

/**
 * Fetch activity feed for dashboard widget (limited entries)
 */
export async function fetchDashboardActivityFeed(
  userId: string
): Promise<ActivityFeedEntry[]> {
  const count = await getActivityFeedDashboardCount();
  return fetchActivityFeed(userId, {
    limit: count,
    offset: 0,
    enabledTypesOnly: true,
  });
}

/**
 * Create a manual activity entry (for custom events)
 */
export async function createActivityEntry(
  userId: string,
  entry: {
    pregnancy_id?: string;
    activity_type: ActivityType;
    title: string;
    description?: string;
    metadata?: Record<string, unknown>;
    activity_date?: string;
  }
): Promise<boolean> {
  try {
    const { error } = await supabase.from('activity_feed').insert({
      user_id: userId,
      pregnancy_id: entry.pregnancy_id || null,
      activity_type: entry.activity_type,
      activity_date: entry.activity_date || new Date().toISOString().split('T')[0],
      title: entry.title,
      description: entry.description || null,
      metadata: entry.metadata || null,
      actor_user_id: userId,
    });

    if (error) {
      console.error('Error creating activity entry:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in createActivityEntry:', error);
    return false;
  }
}

/**
 * Create SSW milestone entry
 */
export async function createSSWMilestoneEntry(
  userId: string,
  pregnancyId: string,
  ssw: number,
  developments: string[]
): Promise<boolean> {
  const milestoneDescriptions: Record<number, string> = {
    12: 'Ende des 1. Trimesters - Das Risiko für Fehlgeburten sinkt deutlich.',
    20: 'Halbzeit! - Dein Baby ist ca. 16 cm groß und Bewegungen werden spürbar.',
    27: 'Ende des 2. Trimesters - Das Baby könnte außerhalb des Mutterleibs überleben.',
    40: 'Geburtstermin erreicht - Dein Baby ist bereit für die Welt!',
  };

  return createActivityEntry(userId, {
    pregnancy_id: pregnancyId,
    activity_type: 'ssw_milestone',
    title: `SSW ${ssw} erreicht`,
    description: milestoneDescriptions[ssw] || `Du bist jetzt in SSW ${ssw}!`,
    metadata: {
      ssw,
      developments,
    },
  });
}

/**
 * Get activity entries grouped by month
 */
export function groupActivitiesByMonth(
  activities: ActivityFeedEntry[]
): Map<string, ActivityFeedEntry[]> {
  const grouped = new Map<string, ActivityFeedEntry[]>();

  activities.forEach((activity) => {
    const date = new Date(activity.activity_date);
    const monthLabel = date.toLocaleDateString('de-DE', {
      month: 'long',
      year: 'numeric',
    });

    if (!grouped.has(monthLabel)) {
      grouped.set(monthLabel, []);
    }
    grouped.get(monthLabel)!.push(activity);
  });

  return grouped;
}

/**
 * Create baby care activity entry
 */
export async function createBabyCareActivity(
  userId: string,
  entry: {
    pregnancy_id?: string;
    care_type: 'feeding' | 'diaper' | 'sleep' | 'medication' | 'temperature' | 'notes';
    title: string;
    description?: string;
    metadata?: Record<string, unknown>;
    recorded_at?: string;
  }
): Promise<boolean> {
  const activityTypeMap: Record<string, ActivityType> = {
    feeding: 'baby_care_feeding',
    diaper: 'baby_care_diaper',
    sleep: 'baby_care_sleep',
    medication: 'baby_care_medication',
    temperature: 'baby_care_temperature',
    notes: 'baby_care_note',
  };

  return createActivityEntry(userId, {
    pregnancy_id: entry.pregnancy_id,
    activity_type: activityTypeMap[entry.care_type] || 'baby_care_note',
    title: entry.title,
    description: entry.description,
    metadata: {
      care_type: entry.care_type,
      ...entry.metadata,
    },
    activity_date: entry.recorded_at
      ? new Date(entry.recorded_at).toISOString().split('T')[0]
      : undefined,
  });
}

/**
 * Format activity date for display
 */
export function formatActivityDate(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Check if it's today
  if (date.toDateString() === today.toDateString()) {
    return 'Heute';
  }

  // Check if it's yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Gestern';
  }

  // Format as "15. Jan" or "15. Jan 2025" if different year
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
  };

  if (date.getFullYear() !== today.getFullYear()) {
    options.year = 'numeric';
  }

  return date.toLocaleDateString('de-DE', options);
}
